import React, { useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Radio } from 'lucide-react';

const ROUND_LABELS = {
  final: 'גמר', semi_final: 'חצי גמר', quarter_final: 'רבע גמר',
  round_of_16: 'שמינית גמר', round_of_32: '1/32', group_stage: 'שלב בתים',
  group_a: 'בית א', group_b: 'בית ב', group_c: 'בית ג', group_d: 'בית ד',
  consolation_r16: 'תנחומים 1/16', consolation_quarter: 'רבע תנחומים',
  consolation_semi: 'חצי תנחומים', consolation_final: 'גמר תנחומים', third_place: 'מקום שלישי',
};

const roundLabel = (m) => ROUND_LABELS[m.round] || (m.group_name ? `בית ${m.group_name}` : m.round);

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (isNaN(diff)) return '';
  if (diff < 60) return 'הרגע';
  if (diff < 3600) return `לפני ${Math.floor(diff / 60)} דק׳`;
  if (diff < 86400) return `לפני ${Math.floor(diff / 3600)} שע׳`;
  return `לפני ${Math.floor(diff / 86400)} ימים`;
};

const LiveResultsFeed = ({ matches = [], players = [], isLive = false }) => {
  const seenRef = useRef(new Set());

  const playerMap = {};
  (players || []).forEach((p) => { if (p && p.id) playerMap[p.id] = p; });

  // קופסאות ניקוד עבור שחקן מסוים (סט אחרי סט) - מדגיש את הסטים שניצח
  const scoreBoxes = (m, side) => {
    const sets = m.sets || [];
    return sets.map((s, i) => {
      const mine = side === 1 ? (s.player1_games ?? s.player1_score ?? 0) : (s.player2_games ?? s.player2_score ?? 0);
      const opp = side === 1 ? (s.player2_games ?? s.player2_score ?? 0) : (s.player1_games ?? s.player1_score ?? 0);
      const win = mine > opp;
      return (
        <span key={i} className={`w-7 h-7 flex items-center justify-center rounded-md font-mono text-sm ${win ? 'bg-emerald-500 text-white font-bold' : 'bg-slate-100 text-slate-500'}`}>{mine}</span>
      );
    });
  };

  // שורת שחקן - אווטאר + שם מלא + ניקוד מיושר לשמאל
  const MobilePlayerRow = ({ id, name, win, m, side, special }) => {
    const p = playerMap[id];
    const avatar = p?.avatar;
    return (
      <div className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${win ? 'bg-emerald-50' : ''}`}>
        <Avatar className="w-7 h-7 shrink-0">
          {avatar && <AvatarImage src={avatar} alt={name} />}
          <AvatarFallback className={`text-[11px] font-bold ${win ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
            {(name || '?').charAt(0)}
          </AvatarFallback>
        </Avatar>
        {win && <span className="text-base shrink-0">🏆</span>}
        <span className={`flex-1 min-w-0 truncate text-sm ${win ? 'font-extrabold text-emerald-700' : 'text-slate-600'}`}>{name}</span>
        <div className="flex gap-1 shrink-0">
          {special ? (
            <span className="text-xs font-bold text-amber-600">{win ? special : ''}</span>
          ) : scoreBoxes(m, side)}
        </div>
      </div>
    );
  };

  const scrollToMatch = (matchId) => {
    const el = document.getElementById(`match-card-${matchId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('match-flash');
    setTimeout(() => el.classList.remove('match-flash'), 2200);
  };

  const completed = matches
    .filter((m) => m.completed && m.winner_id)
    .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0))
    .slice(0, 12);

  if (completed.length === 0) return null;

  const isNew = (id) => !seenRef.current.has(id);
  // mark seen after computing (next render won't animate)
  completed.forEach((m) => seenRef.current.add(m.id));

  return (
    <Card className="mb-6" dir="rtl" data-testid="live-results-feed">
      <style>{`
        @keyframes feedSlideIn {
          0% { opacity: 0; transform: translateY(-10px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .feed-item-new { animation: feedSlideIn 0.5s ease-out; }
        @keyframes matchFlash {
          0%, 60% { box-shadow: 0 0 0 4px rgba(16,185,129,0.55); border-color: #10b981 !important; }
          100% { box-shadow: none; }
        }
        .match-flash { animation: matchFlash 2.2s ease-out; }
      `}</style>
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2 justify-end flex-row-reverse">
          <span className="relative flex items-center gap-2">
            <Radio className={`w-5 h-5 ${isLive ? 'text-red-500' : 'text-emerald-500'}`} />
            {isLive && <span className="absolute -right-1 -top-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-ping" />}
          </span>
          פיד תוצאות {isLive ? 'חי' : 'אחרונות'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {completed.map((m) => {
            const p1win = m.winner_id === m.player1_id;
            const special = m.win_type === 'walkover' ? 'W.O.' : m.win_type === 'retirement' ? 'פרישה' : null;
            return (
              <div
                key={m.id}
                data-testid={`feed-item-${m.id}`}
                onClick={() => scrollToMatch(m.id)}
                title="לחץ לקפיצה למשחק בברקט"
                className={`rounded-xl border border-slate-200 bg-white p-3 hover:shadow-md hover:border-emerald-300 transition-shadow cursor-pointer ${isNew(m.id) ? 'feed-item-new' : ''}`}
              >
                {/* ===== מובייל: שורה לכל שחקן ===== */}
                <div className="sm:hidden">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{roundLabel(m)}</span>
                    <span className="text-[11px] font-bold text-slate-400">{timeAgo(m.updated_at)}</span>
                  </div>
                  <div className="space-y-1">
                    <MobilePlayerRow id={m.player1_id} name={m.player1_name} win={p1win} m={m} side={1} special={special} />
                    <MobilePlayerRow id={m.player2_id} name={m.player2_name} win={!p1win} m={m} side={2} special={special} />
                  </div>
                </div>

                {/* ===== דסקטופ: לוח תוצאות בסגנון ATP - שתי שורות עם סטים מיושרים בעמודות ===== */}
                <div className="hidden sm:grid grid-cols-[7rem_minmax(0,1fr)] items-center gap-4">
                  {/* מטא (ימין ב-RTL) */}
                  <div className="flex flex-col items-start gap-1 min-w-0">
                    <span className="text-[10px] font-bold text-slate-400 truncate w-full">{timeAgo(m.updated_at)}</span>
                    <span className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 truncate max-w-full">{roundLabel(m)}</span>
                  </div>

                  {/* לוח: שורה לכל שחקן, קופסאות הסטים מיושרות זו מתחת לזו */}
                  <div className="space-y-1 max-w-md">
                    <MobilePlayerRow id={m.player1_id} name={m.player1_name} win={p1win} m={m} side={1} special={special} />
                    <MobilePlayerRow id={m.player2_id} name={m.player2_name} win={!p1win} m={m} side={2} special={special} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default LiveResultsFeed;
